import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AlertTriangle, ArrowDown, ArrowUp, Check, LayoutGrid, RotateCcw, Search, X } from 'lucide-react';
import '../analytics.css';
import {
  WIDGET_REGISTRY,
  WIDGET_GROUP_LABELS,
  WIDGET_GROUP_ORDER,
  WIDGET_SIZE_LABELS,
  STARTER_WIDGET_KEYS,
  getWidgetDefinition,
  sanitizeWidgetKeys,
  type WidgetDefinition,
  type WidgetGroup,
} from '../widgetRegistry.tsx';
import { PageHeader } from '../controller/shared.tsx';
import { CategoryBadge, WidgetPreview } from './WidgetPreview.tsx';
import {
  useDashboardWidgetSelection,
  useResetDashboardWidgets,
  useSaveDashboardWidgets,
} from '../../../hooks/dashboards/useDashboardLayout.ts';
import { resolve403ErrorMessage } from '../../../request/getApiErrorMessage.ts';
import { TENANT_PATHS } from '../../../routing/paths.ts';

/** Renders the live preview only once the card scrolls near the viewport — the catalog has ~55 of them. */
const LazyPreview: React.FC<{ widget: WidgetDefinition }> = ({ widget }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (visible || !ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <div ref={ref} className="h-[108px]">
      {visible ? <WidgetPreview widget={widget} /> : <div className="h-full rounded-lg border border-slate-200 bg-slate-50" />}
    </div>
  );
};

function sameOrder(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((key, i) => key === b[i]);
}

/**
 * Settings → Dashboard Widgets. Every user picks which catalog widgets appear
 * on their dashboard and in what order; nothing is saved until "Save".
 * Role-based limits on the catalog are a later phase.
 */
export const DashboardWidgetsSettings: React.FC = () => {
  const selection = useDashboardWidgetSelection();
  const saveMutation = useSaveDashboardWidgets();
  const resetMutation = useResetDashboardWidgets();

  const [draft, setDraft] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState<WidgetGroup | 'all'>('all');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Hydrate the draft once the saved choice arrives. Later server updates
  // (a save or reset here) set the draft explicitly, so a background refetch
  // can never overwrite unsaved ticks.
  const hydrated = useRef(false);
  useEffect(() => {
    if (selection.data && !hydrated.current) {
      hydrated.current = true;
      setDraft(selection.widgetKeys);
    }
  }, [selection.data, selection.widgetKeys]);

  const isLoading = selection.isLoading;
  const isBusy = isLoading || saveMutation.isPending || resetMutation.isPending;
  const isDirty = !!selection.data && !sameOrder(draft, selection.widgetKeys);

  useEffect(() => {
    if (!isDirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  const selectedSet = useMemo(() => new Set(draft), [draft]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const groups: Record<WidgetGroup, WidgetDefinition[]> = {
      overview: [],
      risk: [],
      testing: [],
      results: [],
      issues: [],
      external: [],
      mywork: [],
      review: [],
    };
    for (const widget of WIDGET_REGISTRY) {
      if (groupFilter !== 'all' && widget.group !== groupFilter) continue;
      if (q && !widget.title.toLowerCase().includes(q) && !(widget.description ?? '').toLowerCase().includes(q)) continue;
      groups[widget.group].push(widget);
    }
    return groups;
  }, [search, groupFilter]);

  const groupCounts = useMemo(() => {
    const counts = {} as Record<WidgetGroup, number>;
    for (const group of WIDGET_GROUP_ORDER) counts[group] = 0;
    for (const widget of WIDGET_REGISTRY) counts[widget.group] += 1;
    return counts;
  }, []);

  const resultCount = WIDGET_GROUP_ORDER.reduce((sum, g) => sum + filteredGroups[g].length, 0);

  const toggle = (key: string) => {
    setDraft((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const move = (index: number, delta: -1 | 1) => {
    setDraft((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSave = () => {
    setSaveError(null);
    saveMutation.mutate(draft, {
      onSuccess: () => toast.success('Dashboard widgets saved'),
      onError: (err: any) => {
        const status = err?.response?.status;
        setSaveError(
          status === 403
            ? resolve403ErrorMessage(err, 'You do not have permission to change dashboard widgets.')
            : 'Your widget choice could not be saved. Try again.'
        );
      },
    });
  };

  const handleCancel = () => {
    setSaveError(null);
    setDraft(selection.widgetKeys);
  };

  // Deletes the saved choice, so the user follows the starter set (including
  // future changes to it) instead of a frozen copy.
  const handleResetToStarter = () => {
    setSaveError(null);
    resetMutation.mutate(undefined, {
      onSuccess: (data) => {
        setDraft(data.is_default ? STARTER_WIDGET_KEYS : sanitizeWidgetKeys(data.layout.map((r) => r.widget_key)));
        toast.success('Dashboard reset to the starter set');
      },
      onError: () => setSaveError('Your dashboard could not be reset. Try again.'),
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          icon={<LayoutGrid className="h-5 w-5" />}
          iconClassName="bg-teal-50 text-teal-600"
          title="Dashboard Widgets"
          description={
            isLoading
              ? 'Loading your widgets…'
              : `Choose which reports appear on your dashboard. ${WIDGET_REGISTRY.length} available · ${draft.length} selected`
          }
          actions={
            <>
              <button
                type="button"
                onClick={handleResetToStarter}
                disabled={isBusy || (selection.isDefault && !isDirty)}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCcw className="h-4 w-4" />
                Reset to starter set
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={!isDirty || isBusy}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!isDirty || isBusy}
                className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="h-4 w-4" />
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        />

        {saveError && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {saveError}
          </div>
        )}

        {selection.isError ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-6 text-center text-sm text-rose-600">
            Your saved widgets could not be loaded. Refresh the page to try again.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-start">
            <div className="min-w-0 space-y-6">
              <div className="space-y-3">
                <div className="relative max-w-md">
                  <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
                  <input
                    id="dashboard-widgets-search"
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search widgets…"
                    className="w-full rounded-md border border-slate-300 py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by topic">
                  {(['all', ...WIDGET_GROUP_ORDER] as const).map((group) => {
                    const active = groupFilter === group;
                    const label = group === 'all' ? 'All' : WIDGET_GROUP_LABELS[group];
                    const count = group === 'all' ? WIDGET_REGISTRY.length : groupCounts[group];
                    return (
                      <button
                        key={group}
                        type="button"
                        aria-pressed={active}
                        onClick={() => setGroupFilter(group)}
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                          active
                            ? 'border-teal-600 bg-teal-600 text-white'
                            : 'border-slate-300 bg-white text-slate-600 hover:border-teal-300'
                        }`}
                      >
                        {label} <span className={active ? 'text-teal-100' : 'text-slate-400'}>{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {resultCount === 0 && (
                <p className="rounded-xl border border-dashed border-slate-300 bg-white py-10 text-center text-sm text-slate-400">
                  No widgets match your search.
                </p>
              )}

              {WIDGET_GROUP_ORDER.map((group) => {
                const widgets = filteredGroups[group];
                if (widgets.length === 0) return null;
                return (
                  <section key={group}>
                    <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {WIDGET_GROUP_LABELS[group]} · {widgets.length}
                    </h2>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      {widgets.map((widget) => {
                        const picked = selectedSet.has(widget.widget_key);
                        const inputId = `widget-pick-${widget.widget_key}`;
                        return (
                          <div
                            key={widget.widget_key}
                            onClick={() => !isLoading && toggle(widget.widget_key)}
                            className={`flex cursor-pointer flex-col gap-2 rounded-xl border bg-white p-3 transition-colors ${
                              picked ? 'border-teal-500 ring-1 ring-teal-500' : 'border-slate-200 hover:border-teal-300'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <label htmlFor={inputId} className="min-w-0 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                <span className="block text-sm font-semibold text-slate-900">{widget.title}</span>
                                {widget.description ? (
                                  <span className="mt-0.5 line-clamp-2 block text-[11px] leading-snug text-slate-500">
                                    {widget.description}
                                  </span>
                                ) : null}
                              </label>
                              <input
                                id={inputId}
                                type="checkbox"
                                checked={picked}
                                disabled={isLoading}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggle(widget.widget_key)}
                                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                              />
                            </div>
                            <LazyPreview widget={widget} />
                            <div className="flex flex-wrap items-center gap-1.5">
                              <CategoryBadge category={widget.category} />
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-500">
                                {WIDGET_SIZE_LABELS[widget.size]}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>

            <aside className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:sticky lg:top-4">
              <h2 className="text-sm font-semibold text-slate-900">Your dashboard · {draft.length}</h2>
              <p className="mt-1 text-xs text-slate-500">
                Use the arrows to set the order. The dashboard keeps this order within each size row.
              </p>
              {isLoading ? (
                <div className="mt-4 space-y-2" aria-hidden="true">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="analytics-skeleton h-7 w-full" />
                  ))}
                </div>
              ) : draft.length === 0 ? (
                <p className="mt-4 rounded-lg border border-dashed border-slate-300 px-3 py-6 text-center text-xs text-slate-400">
                  No widgets selected. Tick widgets on the left to add them.
                </p>
              ) : (
                <ol className="mt-3 max-h-[60vh] space-y-1.5 overflow-y-auto analytics-scroll">
                  {draft.map((key, index) => {
                    const def = getWidgetDefinition(key);
                    if (!def) return null;
                    return (
                      <li
                        key={key}
                        className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs"
                      >
                        <span className="w-5 shrink-0 text-right font-mono text-slate-400">{index + 1}</span>
                        <span className="min-w-0 flex-1 truncate font-medium text-slate-700" title={def.title}>
                          {def.title}
                        </span>
                        <button
                          type="button"
                          onClick={() => move(index, -1)}
                          disabled={index === 0}
                          aria-label={`Move ${def.title} up`}
                          className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700 disabled:opacity-30"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => move(index, 1)}
                          disabled={index === draft.length - 1}
                          aria-label={`Move ${def.title} down`}
                          className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700 disabled:opacity-30"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggle(key)}
                          aria-label={`Remove ${def.title}`}
                          className="rounded p-0.5 text-slate-400 hover:bg-rose-100 hover:text-rose-600"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    );
                  })}
                </ol>
              )}
              <Link
                to={TENANT_PATHS.ANALYTICS_DASHBOARD}
                className="mt-4 block rounded-md border border-slate-300 px-3 py-1.5 text-center text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {isDirty ? 'Open dashboard (unsaved changes are lost)' : 'Open dashboard'}
              </Link>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardWidgetsSettings;
